/**
 * Report a Loss — first notice of loss to the carrier.
 *
 * ⚠️ THE ACKNOWLEDGMENT IS THE PRODUCT, NOT THE SEND. The exposure in FNOL is
 * late reporting or reporting to the wrong place, either of which can prejudice
 * a claim. So what opens is the list of notices with no claim number against
 * them, ordered by how long they have been waiting — the thing somebody has to
 * act on — with the form behind a button.
 *
 * ⚠️ AND IT ONLY OFFERS CLAIMS CONTACTS. Loss runs fall back to an underwriter,
 * who will forward a records request. A first notice sent to underwriting is a
 * delayed claim, so the picker asks for `purpose=claims` and shows nothing at
 * all rather than something plausible.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../auth/AuthProvider';
import { useNavigate } from 'react-router-dom';
import { moduleById } from '@commpass/shared';
import { PageHero } from '../components/PageHero';
import { openedOnForm, prefill } from '../lib/newForm';
import { receiptState } from '../lib/receipt';
import { StatCard } from '../components/StatCard';
import { ApiError, api, type FnolReport, type LossType } from '../lib/api';
import { formatPhone } from '../lib/phone';

const LOSS_TYPES: { value: LossType; label: string }[] = [
  { value: 'property', label: 'Property' },
  { value: 'auto', label: 'Automobile' },
  { value: 'general-liability', label: 'General Liability' },
  { value: 'workers-comp', label: "Workers' Comp" },
  { value: 'other', label: 'Other' },
];

const STATUS_LABEL: Record<FnolReport['status'], string> = {
  sent: 'Awaiting claim number',
  chased: 'Chased',
  acknowledged: 'Acknowledged',
  closed: 'Closed',
};

const daysSince = (iso: string | null): number =>
  iso ? Math.floor((Date.now() - new Date(iso).getTime()) / 86400000) : 0;

/** "2026-09-03" → "09/03/2026", without new Date() and its timezone shift. */
const usDate = (iso: string): string => {
  const [y, m, d] = String(iso ?? '').split('-');
  return y && m && d ? `${m}/${d}/${y}` : String(iso ?? '');
};

const longDate = (iso: string): string =>
  new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

/**
 * Initials for the monogram tile.
 *
 * ⚠️ A MONOGRAM, NOT A LOGO. The design this came from shows carrier logos and
 * we hold no logo assets; a generic placeholder repeated down the list would say
 * less than the carrier's own initials. Company noise is dropped first so
 * "The Hartford CL" reads HC rather than TH.
 */
function initialsOf(name: string): string {
  const words = String(name ?? '')
    .replace(/[^A-Za-z0-9 ]/g, ' ')
    .split(/\s+/)
    .filter((w) => w && !/^(the|of|and|inc|llc|ltd|co|company|insurance|group)$/i.test(w));
  const pick = words.length ? words : String(name ?? '').split(/\s+/).filter(Boolean);
  return pick.slice(0, 2).map((w) => w[0]?.toUpperCase() ?? '').join('') || '?';
}

const FN_TABS = [
  { key: 'open', label: 'Awaiting a claim number' },
  { key: 'acknowledged', label: 'Acknowledged' },
  { key: 'closed', label: 'Closed' },
  { key: 'all', label: 'All' },
] as const;

type FnTab = (typeof FN_TABS)[number]['key'];

const fileToBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).split(',')[1] ?? '');
    r.onerror = () => reject(new Error('Could not read that file.'));
    r.readAsDataURL(file);
  });


/** Where a finished request lands. The list it joins, not the form it came from. */
const REQUESTS_HREF = moduleById('requests')?.href ?? '/hub/requests';

export function ReportLoss(): JSX.Element {
  const navigate = useNavigate();
  const { email } = useAuth();

  const [rows, setRows] = useState<FnolReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState('');
  // Straight onto the form when the Requests Center sent you here to make one.
  const [showForm, setShowForm] = useState(openedOnForm);

  // Which row is having a claim number typed into it.
  const [ackFor, setAckFor] = useState('');
  const [claimNumber, setClaimNumber] = useState('');

  const [tab, setTab] = useState<FnTab>('open');
  const [query, setQuery] = useState('');

  /*
    ⚠️ SEEDED FROM THE URL, THEN ORDINARY STATE. Connor hands these over when he
    starts a request for somebody (see lib/newForm.ts). A useState initialiser
    rather than an effect, so the field is filled on the first paint — an effect
    would show it empty and then fill it, which reads as the page correcting
    itself and makes people re-check what they just watched change.
  */
  const [insuredName, setInsuredName] = useState(() => prefill().insured ?? '');
  const [policyNumber, setPolicyNumber] = useState('');
  const [carrierName, setCarrierName] = useState(() => prefill().carrier ?? '');
  const [carrierOpen, setCarrierOpen] = useState(false);
  const [lossType, setLossType] = useState<LossType>('property');
  const [lossDate, setLossDate] = useState('');
  const [lossTime, setLossTime] = useState('');
  const [lossLocation, setLossLocation] = useState('');
  const [description, setDescription] = useState('');
  const [contactName, setContactName] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [injuries, setInjuries] = useState(false);
  const [authorities, setAuthorities] = useState('');
  const [replyTo, setReplyTo] = useState('');
  const [picked, setPicked] = useState<string[]>([]);
  const [typedTo, setTypedTo] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [suggested, setSuggested] = useState<{ name: string; email: string; role: string }[]>([]);
  const [matched, setMatched] = useState<string | null>(null);
  const [nearby, setNearby] = useState<{ name: string; hasContact: boolean }[]>([]);

  const [draft, setDraft] = useState<{ subject: string; bodyText: string } | null>(null);
  const [edited, setEdited] = useState(false);

  // Same signature block as loss runs, and the same storage key — it is the
  // same person signing, and asking twice would be asking twice.
  const [sig, setSig] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('lr-signature') ?? '{}') as Record<string, string>;
    } catch {
      return {} as Record<string, string>;
    }
  });
  const setSigField = (k: string, v: string) => {
    const next = { ...sig, [k]: v };
    setSig(next);
    try {
      localStorage.setItem('lr-signature', JSON.stringify(next));
    } catch {
      // A browser refusing storage must not stop somebody reporting a loss.
    }
  };

  useEffect(() => {
    if (email && !replyTo) setReplyTo(email);
  }, [email, replyTo]);

  const load = useCallback(async () => {
    try {
      const r = await api.fnolList();
      setRows(r.reports);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Could not load your notices.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!carrierName.trim()) {
      setSuggested([]);
      setMatched(null);
      setNearby([]);
      return;
    }
    const t = setTimeout(async () => {
      try {
        const r = await api.carrierContacts(carrierName, 'claims');
        setSuggested(r.contacts);
        setMatched(r.matched ?? null);
        setNearby(r.carriers ?? []);
      } catch {
        setSuggested([]);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [carrierName]);

  const recipients = useMemo(() => {
    const typed = typedTo
      .split(/[,;\s]+/)
      .map((s) => s.trim())
      .filter((s) => s.includes('@'));
    return [...new Set([...picked, ...typed])];
  }, [picked, typedTo]);

  const ready =
    insuredName.trim() && carrierName.trim() && lossDate && description.trim() && replyTo.includes('@');
  const canSend = ready && recipients.length > 0;

  const fields = () => ({
    insuredName: insuredName.trim(),
    policyNumber: policyNumber.trim(),
    lossType,
    lossDate,
    lossTime: lossTime.trim(),
    lossLocation: lossLocation.trim(),
    description: description.trim(),
    contactName: contactName.trim(),
    contactPhone: contactPhone.trim(),
    injuries,
    authorities: authorities.trim(),
    reporterName: (sig.name ?? '').trim(),
    agencyName: (sig.agency ?? '').trim(),
    reporterPhone: (sig.phone ?? '').trim(),
    replyTo: replyTo.trim(),
  });

  async function toReview() {
    setBusy('preview');
    setErr('');
    try {
      setDraft(await api.fnolPreview(fields()));
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Could not build the notice.');
    } finally {
      setBusy('');
    }
  }

  function backToForm() {
    if (edited && !window.confirm('Your changes to the notice will be lost. Go back?')) return;
    setDraft(null);
    setEdited(false);
  }

  async function send() {
    if (!canSend) return;
    setBusy('send');
    setErr('');
    try {
      const payload = await Promise.all(
        files.map(async (f) => ({
          name: f.name,
          mime: f.type || 'application/octet-stream',
          data: await fileToBase64(f),
          size: f.size,
        })),
      );
      const r = await api.fnolCreate({
        ...fields(),
        carrierName: (matched ?? carrierName).trim(),
        sendTo: recipients,
        files: payload,
        ...(draft ? { bodyText: draft.bodyText, subject: draft.subject } : {}),
      });
      // Back to the dashboard the notice now appears on. See lib/receipt.ts.
      navigate(
        REQUESTS_HREF,
        receiptState({
          id: r.id,
          headline: `Loss reported to ${(matched ?? carrierName).trim()}. It stays outstanding until you record the claim number.`,
        }),
      );
      setShowForm(false);
      setDraft(null);
      setEdited(false);
      setInsuredName('');
      setPolicyNumber('');
      setCarrierName('');
      setLossDate('');
      setLossTime('');
      setLossLocation('');
      setDescription('');
      setContactName('');
      setContactPhone('');
      setInjuries(false);
      setAuthorities('');
      setPicked([]);
      setTypedTo('');
      setFiles([]);
      await load();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Could not send it.');
    } finally {
      setBusy('');
    }
  }

  async function act(id: string, fn: () => Promise<unknown>) {
    setBusy(id);
    setErr('');
    try {
      await fn();
      await load();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Could not update it.');
    } finally {
      setBusy('');
    }
  }

  const open = rows.filter((r) => r.status === 'sent' || r.status === 'chased');

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rows
      .filter((r) =>
        tab === 'all'
          ? true
          : tab === 'open'
            ? r.status === 'sent' || r.status === 'chased'
            : r.status === tab,
      )
      .filter(
        (r) =>
          !q ||
          `${r.insured_name} ${r.carrier_name} ${r.id} ${r.policy_number} ${r.claim_number}`
            .toLowerCase()
            .includes(q),
      )
      .sort((a, b) => a.created_at.localeCompare(b.created_at));
  }, [rows, tab, query]);

  const monthName = new Date().toLocaleDateString('en-US', { month: 'long' });

  /**
   * ⚠️ AVERAGE ONLY WHERE AN AVERAGE MEANS SOMETHING. Time-to-acknowledge is a
   * genuine average — every carrier eventually answers or the notice is closed.
   * The one number that is NOT averaged is what is still waiting, on the card
   * above: a mean would hide the loss reported three weeks ago that nobody has
   * acknowledged, which is the only one anybody has to chase.
   */
  const stats = useMemo(() => {
    const acked = rows.filter((r) => r.status === 'acknowledged' && r.acknowledged_at);
    const spans = acked.map(
      (r) =>
        Math.max(0, (new Date(r.acknowledged_at as string).getTime() - new Date(r.created_at).getTime())) / 86400000,
    );
    const now = new Date();
    return {
      awaiting: open.length,
      thisMonth: rows.filter((r) => {
        const d = new Date(r.created_at);
        return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
      }).length,
      acknowledged: acked.length,
      avg: spans.length ? Math.round((spans.reduce((a, b) => a + b, 0) / spans.length) * 10) / 10 : null,
    };
  }, [rows, open]);

  return (
    <div className="wrap">
      <PageHero
        title="Report a loss"
        sub="Tell the carrier about a loss, and keep the record of when you did and what came back."
        action={
          <button
            className="btn btn-primary"
            type="button"
            onClick={() => {
              if (showForm) {
                setDraft(null);
                setEdited(false);
              }
              setShowForm((s) => !s);
            }}
          >
            {showForm ? 'Cancel' : 'Report a loss'}
          </button>
        }
      />

      {err && <p className="is-warn">{err}</p>}


      {showForm && (
        <ol className="lr-steps">
          <li className={draft ? '' : 'is-on'}>1 · The loss</li>
          <li className={draft ? 'is-on' : ''}>2 · The notice</li>
        </ol>
      )}

      {showForm && !draft && (
        <section className="panel lr-form">
          <div className="sf-fields lr-fields">
            <div className="sf-field">
              <label htmlFor="fn-insured">Named insured</label>
              <input id="fn-insured" type="text" value={insuredName} onChange={(e) => setInsuredName(e.target.value)} />
            </div>
            <div className="sf-field">
              <label htmlFor="fn-policy">Policy number</label>
              <input id="fn-policy" type="text" value={policyNumber} onChange={(e) => setPolicyNumber(e.target.value)} />
              <p className="sf-help">If you have it.</p>
            </div>

            <div className="sf-field lr-combo">
              <label htmlFor="fn-carrier">Carrier</label>
              <input
                id="fn-carrier"
                type="text"
                autoComplete="off"
                value={carrierName}
                onChange={(e) => {
                  setCarrierName(e.target.value);
                  setCarrierOpen(true);
                }}
                onFocus={() => setCarrierOpen(true)}
                onBlur={() => setTimeout(() => setCarrierOpen(false), 150)}
                placeholder="Start typing, or pick from the list"
              />
              {carrierOpen && nearby.length > 0 && (
                <ul className="lr-options">
                  {nearby.map((c) => (
                    <li key={c.name}>
                      <button
                        type="button"
                        onClick={() => {
                          setCarrierName(c.name);
                          setCarrierOpen(false);
                        }}
                      >
                        <span>{c.name}</span>
                        {c.hasContact && <span className="lr-hascontact">claims</span>}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              <p className="sf-help">Tagged where we hold a claims contact.</p>
            </div>

            <div className="sf-field">
              <span className="lr-sublabel">Type of loss</span>
              <div className="lr-lines">
                {LOSS_TYPES.map((t) => (
                  <label key={t.value} className={lossType === t.value ? 'is-on' : ''}>
                    <input
                      type="radio"
                      name="lossType"
                      checked={lossType === t.value}
                      onChange={() => setLossType(t.value)}
                    />
                    {t.label}
                  </label>
                ))}
              </div>
            </div>

            <div className="sf-field">
              <label htmlFor="fn-date">Date of loss</label>
              <div className="lr-dates">
                <input id="fn-date" type="date" value={lossDate} onChange={(e) => setLossDate(e.target.value)} />
                <input
                  type="time"
                  aria-label="Time of loss"
                  value={lossTime}
                  onChange={(e) => setLossTime(e.target.value)}
                />
              </div>
              <p className="sf-help">Time if known — it matters on an auto loss.</p>
            </div>
            <div className="sf-field">
              <label htmlFor="fn-location">Where it happened</label>
              <input
                id="fn-location"
                type="text"
                value={lossLocation}
                onChange={(e) => setLossLocation(e.target.value)}
              />
            </div>

            <div className="sf-field is-wide is-textarea">
              <label htmlFor="fn-desc">What happened</label>
              <textarea id="fn-desc" rows={4} value={description} onChange={(e) => setDescription(e.target.value)} />
              <p className="sf-help">
                In the insured&rsquo;s own words where you can. Facts only — a carrier decides liability, not us.
              </p>
            </div>

            <div className="sf-field">
              <label htmlFor="fn-contact">Contact for the adjuster</label>
              <input id="fn-contact" type="text" value={contactName} onChange={(e) => setContactName(e.target.value)} />
              <p className="sf-help">Often not you.</p>
            </div>
            <div className="sf-field">
              <label htmlFor="fn-contactphone">Their phone</label>
              <input
                id="fn-contactphone"
                type="tel"
                value={contactPhone}
                onChange={(e) => setContactPhone(e.target.value)}
                onBlur={(e) => setContactPhone(formatPhone(e.target.value))}
              />
            </div>

            <div className="sf-field">
              <span className="lr-sublabel">Injuries</span>
              <div className="lr-lines">
                <label className={injuries ? 'is-on' : ''}>
                  <input type="checkbox" checked={injuries} onChange={(e) => setInjuries(e.target.checked)} />
                  Someone was injured
                </label>
              </div>
              <p className="sf-help">Carriers treat these differently, and faster.</p>
            </div>
            <div className="sf-field">
              <label htmlFor="fn-auth">Police or fire report</label>
              <input id="fn-auth" type="text" value={authorities} onChange={(e) => setAuthorities(e.target.value)} />
              <p className="sf-help">Reference number, if there is one.</p>
            </div>
          </div>

          <div className="lr-contacts">
            <p className="lr-legend">Send to</p>
            {suggested.length > 0 ? (
              <>
                <p className="sf-help">
                  Claims contacts we hold for <b>{matched}</b>.
                </p>
                {suggested.map((c) => (
                  <label key={c.email} className="lr-contact">
                    <input
                      type="checkbox"
                      checked={picked.includes(c.email)}
                      onChange={(e) =>
                        setPicked((p) => (e.target.checked ? [...p, c.email] : p.filter((x) => x !== c.email)))
                      }
                    />
                    <span>
                      <b>{c.name || c.email}</b> <span className="lr-role">{c.role}</span>
                      <br />
                      <span className="sf-help">{c.email}</span>
                    </span>
                  </label>
                ))}
              </>
            ) : (
              <p className="sf-help">
                {carrierName.trim()
                  ? 'We hold no claims contact for that carrier — type the address below. A loss notice must go to claims, so we do not suggest an underwriter.'
                  : 'Type a carrier above and we will suggest their claims desk.'}
              </p>
            )}
            <div className="sf-field is-wide">
              <label htmlFor="fn-other">Other addresses</label>
              <input
                id="fn-other"
                type="text"
                value={typedTo}
                onChange={(e) => setTypedTo(e.target.value)}
                placeholder="claims@carrier.com"
              />
              <p className="sf-help">Comma separated.</p>
            </div>
          </div>

          <div className="sf-fields lr-fields">
            <div className="sf-field">
              <label htmlFor="fn-replyto">Carrier should reply to</label>
              <input id="fn-replyto" type="text" value={replyTo} onChange={(e) => setReplyTo(e.target.value)} />
            </div>
            <div className="sf-field">
              <span className="lr-sublabel">Photos or documents</span>
              <label className="lr-file">
                <input type="file" multiple onChange={(e) => setFiles(Array.from(e.target.files ?? []))} />
                <span className="btn btn-ghost btn-sm">Attach a file</span>
              </label>
              {files.length > 0 ? (
                <ul className="lr-filelist">
                  {files.map((f) => (
                    <li key={f.name}>
                      <span>{f.name}</span>
                      <button
                        type="button"
                        aria-label={`Remove ${f.name}`}
                        onClick={() => setFiles((p) => p.filter((x) => x.name !== f.name))}
                      >
                        ×
                      </button>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="sf-help">Optional.</p>
              )}
            </div>
          </div>

          <p className="lr-legend">Who is reporting it</p>
          <p className="sf-help lr-sighint">Remembered on this device.</p>
          <div className="sf-fields lr-fields">
            <div className="sf-field">
              <label htmlFor="fn-signame">Your name</label>
              <input id="fn-signame" type="text" value={sig.name ?? ''} onChange={(e) => setSigField('name', e.target.value)} />
            </div>
            <div className="sf-field">
              <label htmlFor="fn-sigagency">Agency</label>
              <input id="fn-sigagency" type="text" value={sig.agency ?? ''} onChange={(e) => setSigField('agency', e.target.value)} />
            </div>
            <div className="sf-field">
              <label htmlFor="fn-sigphone">Phone</label>
              <input
                id="fn-sigphone"
                type="tel"
                value={sig.phone ?? ''}
                onChange={(e) => setSigField('phone', e.target.value)}
                onBlur={(e) => setSigField('phone', formatPhone(e.target.value))}
              />
            </div>
          </div>

          <div className="lr-send">
            <button className="btn btn-primary" type="button" disabled={!ready || busy === 'preview'} onClick={toReview}>
              {busy === 'preview' ? 'Building…' : ready ? 'Next — review the notice' : 'Fill in the loss details'}
            </button>
          </div>
        </section>
      )}

      {showForm && draft && (
        <section className="panel lr-review">
          <div className="lr-meta">
            <div>
              <span>To</span>
              <b>{recipients.length ? recipients.join(', ') : 'nobody yet — go back and pick a claims contact'}</b>
            </div>
            <div>
              <span>Replies go to</span>
              <b>{replyTo}</b>
            </div>
            {files.length > 0 && (
              <div>
                <span>Attached</span>
                <b>{files.map((f) => f.name).join(', ')}</b>
              </div>
            )}
          </div>

          <div className="sf-field lr-fields">
            <label htmlFor="fn-subject">Subject</label>
            <input
              id="fn-subject"
              type="text"
              value={draft.subject}
              onChange={(e) => {
                setDraft({ ...draft, subject: e.target.value });
                setEdited(true);
              }}
            />
          </div>

          <div className="sf-field lr-fields">
            <label htmlFor="fn-body">The notice</label>
            <textarea
              id="fn-body"
              className="lr-body"
              rows={22}
              value={draft.bodyText}
              onChange={(e) => {
                setDraft({ ...draft, bodyText: e.target.value });
                setEdited(true);
              }}
            />
            <p className="sf-help lr-footnote">
              A reference line is added underneath so the carrier&rsquo;s reply finds its way back to you.
            </p>
          </div>

          <div className="lr-send">
            <button className="btn btn-ghost" type="button" onClick={backToForm}>
              Back
            </button>
            <button className="btn btn-primary" type="button" disabled={!canSend || busy === 'send'} onClick={send}>
              {busy === 'send' ? 'Sending…' : `Send to ${recipients.length}`}
            </button>
          </div>
        </section>
      )}

      {!showForm && (
        <>
          <div className="stat-row">
            <StatCard
              tone="amber"
              icon="M18 8a6 6 0 10-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9M10 21a2 2 0 004 0"
              label="Awaiting claim number"
              value={stats.awaiting}
              note="Losses waiting on the carrier"
              loading={loading}
              slow={stats.awaiting > 0}
            />
            <StatCard
              tone="blue"
              icon="M6 3h9l4 4v14H6zM14 3v5h5M9 13h7M9 17h5"
              label="Reported this month"
              value={stats.thisMonth}
              note={monthName}
              loading={loading}
            />
            <StatCard
              tone="green"
              icon="M12 3a9 9 0 100 18 9 9 0 000-18zM8 12l3 3 5-5"
              label="Acknowledged"
              value={stats.acknowledged}
              note="Carrier gave a claim number"
              loading={loading}
            />
            {/* ⚠️ CALENDAR DAYS, NOT BUSINESS DAYS. The mock says "business
                days"; we do not hold a holiday calendar, and a number labelled
                business days that quietly counts weekends is worse than an
                honest one. */}
            <StatCard
              tone="violet"
              icon="M12 7v5l3 2M12 3a9 9 0 100 18 9 9 0 000-18z"
              label="Average response"
              value={stats.avg === null ? '—' : stats.avg}
              unit={stats.avg === null ? undefined : 'days'}
              note={stats.avg === null ? 'Nothing acknowledged yet' : 'To a claim number'}
              loading={loading}
            />
          </div>

          <section className="panel lr-table-panel">
            <div className="lr-toolbar">
              <div className="lr-tabs" role="tablist">
                {FN_TABS.map((t) => (
                  <button
                    key={t.key}
                    role="tab"
                    type="button"
                    aria-selected={tab === t.key}
                    className={tab === t.key ? 'is-on' : ''}
                    onClick={() => setTab(t.key)}
                  >
                    {t.label}
                    {t.key === 'open' && open.length > 0 && <span className="lr-count">{open.length}</span>}
                  </button>
                ))}
              </div>
              <div className="lr-search">
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M11 4a7 7 0 100 14 7 7 0 000-14zM20 20l-4-4" />
                </svg>
                <input
                  type="search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search losses…"
                  aria-label="Search losses"
                />
              </div>
            </div>

            {loading ? (
              <p className="lr-empty">Loading…</p>
            ) : !visible.length ? (
              <p className="lr-empty">
                {rows.length ? 'Nothing matches that.' : 'No losses reported yet.'}
                <span>
                  {rows.length
                    ? 'Try another tab, or clear the search.'
                    : 'Report one and it stays here until the carrier gives you a claim number.'}
                </span>
              </p>
            ) : (
              <>
                <ul className="fn-list">
                  {visible.map((r) => {
                    const waiting = daysSince(r.created_at);
                    const isOpen = r.status === 'sent' || r.status === 'chased';
                    return (
                      <li key={r.id} className={isOpen && waiting >= 3 ? 'is-stale' : ''}>
                        <span className="lr-avatar is-carrier" aria-hidden="true">
                          {initialsOf(r.carrier_name)}
                        </span>

                        <div className="fn-who">
                          <b>{r.insured_name}</b>
                          <span>{r.carrier_name}</span>
                          <span className="fn-ref">{r.id}</span>
                        </div>

                        <div className="fn-field">
                          <span>Date of loss</span>
                          <b>{usDate(r.loss_date)}</b>
                        </div>

                        <div className="fn-field">
                          <span>Reported</span>
                          <b>{waiting === 0 ? 'Today' : longDate(r.created_at)}</b>
                        </div>

                        <div className="fn-field">
                          <span>Status</span>
                          <span className={`pill lr-${r.status}`}>{STATUS_LABEL[r.status]}</span>
                          {r.claim_number && <b className="fn-claim">{r.claim_number}</b>}
                        </div>

                        <div className="fn-actions">
                          {isOpen &&
                            (ackFor === r.id ? (
                              <>
                                <input
                                  className="lr-claiminput"
                                  type="text"
                                  autoFocus
                                  placeholder="Claim number"
                                  value={claimNumber}
                                  onChange={(e) => setClaimNumber(e.target.value)}
                                />
                                <button
                                  className="btn btn-sm btn-primary"
                                  type="button"
                                  disabled={!claimNumber.trim() || busy === r.id}
                                  onClick={() =>
                                    act(r.id, async () => {
                                      await api.fnolAcknowledge(r.id, claimNumber.trim());
                                      setAckFor('');
                                      setClaimNumber('');
                                    })
                                  }
                                >
                                  Save
                                </button>
                                <button className="btn btn-sm btn-ghost" type="button" onClick={() => setAckFor('')}>
                                  Cancel
                                </button>
                              </>
                            ) : (
                              <>
                                <button
                                  className="btn btn-sm"
                                  type="button"
                                  disabled={busy === r.id}
                                  onClick={() => act(r.id, () => api.fnolChase(r.id))}
                                >
                                  Follow up
                                </button>
                                <button
                                  className="btn btn-sm btn-primary"
                                  type="button"
                                  onClick={() => {
                                    setAckFor(r.id);
                                    setClaimNumber('');
                                  }}
                                >
                                  Add claim number
                                </button>
                                <button
                                  className="btn btn-sm btn-ghost"
                                  type="button"
                                  disabled={busy === r.id}
                                  onClick={() => act(r.id, () => api.fnolClose(r.id))}
                                >
                                  Close
                                </button>
                              </>
                            ))}
                        </div>
                      </li>
                    );
                  })}
                </ul>

                <p className="lr-count-line">
                  Showing {visible.length} of {rows.length} loss{rows.length === 1 ? '' : 'es'}
                </p>
              </>
            )}
          </section>

          <section className="panel lr-about">
            <div>
              <h3>
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M12 3a9 9 0 100 18 9 9 0 000-18zM12 11v5M12 8h.01" />
                </svg>
                Reporting a loss
              </h3>
              <p>
                Report it to the carrier as soon as the insured tells you, even if you do not have
                every detail yet. Late reporting is what prejudices a claim, and a notice with gaps
                beats a complete one that arrives a week later.
              </p>
              <p className="lr-about-note">
                The claim number the carrier sends back is the proof it was reported. Record it here
                and the follow-ups stop.
              </p>
            </div>
            <div>
              <h3>What to have ready</h3>
              <ul>
                <li>Policy number and the carrier who wrote it</li>
                <li>Date, time and location of the loss</li>
                <li>What happened, in the insured’s own words</li>
                <li>Who the adjuster should ring — often not you</li>
                <li>Any police or fire report reference</li>
              </ul>
            </div>
          </section>
        </>
      )}
    </div>
  );
}
